'use client'
import type { Data } from '@repo/strapi'
import { useTranslations } from 'next-intl'
import AppLink from '@/components/elementary/AppLink'
import { ArrowRight } from '@/components/icons/ArrowRight'
import { BasicImage } from '@/components/page-builder/components/BasicImage'
import Heading, { type HeadingTag } from '@/components/typography/Heading'
import { Paragraph } from '@/components/typography/Paragraph'
import { getBlogFormattedDate } from '@/lib/dates'
import { removeHtmlTags } from '@/lib/html'
import { cn } from '@/lib/styles'
import { BLOG_CARD_TEST_IDS } from '@/lib/test-ids'
import { getArticleLink, getAuthorLink, getBlogCategoryLink } from '@/utils/links'
import styles from './BlogCard.module.css'

export function BlogCard({
	article,
	showLink = false,
	className,
	isAboveTheFold = false,
	titleTag = 'h2',
}: {
	article: Data.ContentType<'api::blog-article.blog-article'>
	showLink?: boolean | null
	className?: string
	isAboveTheFold?: boolean // the images above the fold should load with priority to optimize user experience
	titleTag?: HeadingTag
}) {
	const t = useTranslations('BlogCard')
	const htmlContent =
		article.perex ??
		article.content
			?.find((item) => item.__component === 'global.ck-editor-content')
			?.htmlContent?.substring(0, 500) ??
		''

	const formattedDate = getBlogFormattedDate(
		article.initialPublishedAt,
		article.modifiedAt,
		t('updatedOn'),
	)

	return (
		<article
			className={cn(
				'group/blog-card relative flex flex-col w-full items-start text-left',
				styles['blog-card'],
				className,
			)}
			data-test-id={BLOG_CARD_TEST_IDS.COMPONENT}
		>
			<AppLink
				href={getArticleLink(article.slug)}
				className="absolute inset-0 w-full h-full z-1"
				data-test-id={BLOG_CARD_TEST_IDS.CARD_LINK}
				{...(article.title && { 'aria-label': article.title })}
			/>

			<div className="relative overflow-hidden rounded-xl h-60 w-full mb-4 lg:mb-6">
				<BasicImage
					fill
					component={article.image}
					className="object-cover"
					fetchPriority={isAboveTheFold ? 'high' : 'auto'}
					loading={isAboveTheFold ? 'eager' : 'lazy'}
				/>
			</div>
			<div className="flex flex-wrap gap-1 items-center mb-2">
				{article.blogCategories
					?.filter((category) => category.slug)
					.map((category, index) => (
						<span key={category.id} className="flex items-center">
							<span className={cn('mx-1 leading-none', index === 0 && 'hidden')}>·</span>
							<AppLink
								variant="quaternaryLink"
								className={cn('no-underline lok-text-body-medium', styles['category-link'])}
								href={getBlogCategoryLink(category.slug)}
								data-test-id={BLOG_CARD_TEST_IDS.CATEGORY_LINK}
							>
								{category.name}
							</AppLink>
						</span>
					))}
			</div>

			<Heading
				tag={titleTag}
				fontSize="serifSmall"
				className={cn(
					'group-hover/blog-card:text-brand-salmon group-active/blog-card:text-brand-salmon group-focus-visible/blog-card:text-brand-salmon transition-colors ease-in-out duration-200 line-clamp-3 break-word mb-4',
					styles.title,
				)}
			>
				{article.title}
			</Heading>

			{htmlContent && (
				<Paragraph
					fontSize="bodyRegularMedium"
					className="text-ellipsis overflow-hidden line-clamp-4 break-word mb-4"
				>
					{removeHtmlTags(htmlContent)}
				</Paragraph>
			)}

			{showLink ? (
				<div className="flex items-center gap-1">
					<Paragraph
						fontSize="bodyBoldMedium"
						className="underline group-hover/blog-card:text-brand-salmon group-active/blog-card:text-brand-salmon group-focus-visible/blog-card:text-brand-salmon transition-colors ease-in-out duration-200"
					>
						{t('readMore')} <span className="sr-only">{article.title}</span>
					</Paragraph>

					<ArrowRight
						className={cn(
							'size-4 group-hover/blog-card:text-brand-salmon group-active/blog-card:text-brand-salmon group-focus-visible/blog-card:text-brand-salmon',
							'ml-0 group-hover/blog-card:ml-1 transition-[margin] duration-200',
						)}
					/>
				</div>
			) : (
				<div className="lg:text-lg/5 text-sm/1 text-neutral-light-alpha-500 flex items-center flex-wrap">
					{formattedDate ? <span className="lok-text-body-small">{formattedDate}</span> : null}
					{article.author?.name && (
						<>
							{formattedDate ? <span className="mx-1 leading-none">·</span> : null}
							<span>
								<AppLink
									variant="primaryLink"
									className={cn('text-light lok-text-body-small', styles['author-link'])}
									href={getAuthorLink(article.author.slug)}
									data-test-id={BLOG_CARD_TEST_IDS.AUTHOR_LINK}
								>
									{article.author.name}
								</AppLink>
							</span>
						</>
					)}
				</div>
			)}
		</article>
	)
}
